import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/shared";
import { CURRENCIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RefreshCw, Upload, CreditCard, Bell } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user.organization?.name || "");
  const [currency, setCurrency] = useState(user.organization?.base_currency || "NGN");
  const [saving, setSaving] = useState(false);
  const [fx, setFx] = useState({ base: currency, mode: "manual", rates: {} });
  const [refreshing, setRefreshing] = useState(false);

  const loadFx = useCallback(async () => {
    const r = await api.get("/fx/rates");
    setFx(r.data);
  }, []);
  useEffect(() => { loadFx(); }, [loadFx]);

  const saveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/organization", { name, base_currency: currency });
      await refreshMe();
      toast.success("Organization settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setRate = (code, val) => setFx((f) => ({ ...f, rates: { ...f.rates, [code]: parseFloat(val || 0) } }));

  const saveManualRates = async () => {
    try {
      await api.put("/organization", { fx_mode: "manual", fx_rates: fx.rates });
      toast.success("Exchange rates saved");
      loadFx();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const fetchLive = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/fx/refresh");
      setFx(r.data);
      toast.success("Live rates fetched");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  const setMode = async (mode) => {
    setFx((f) => ({ ...f, mode }));
    if (mode === "live") { fetchLive(); return; }
    try {
      await api.put("/organization", { fx_mode: "manual" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not switch rate mode");
    }
  };

  const otherCurrencies = CURRENCIES.filter((c) => c.code !== fx.base);

  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const sub = user.subscription || {};

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/organization/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      toast.success("Logo updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const requestRenewal = async () => {
    try { const r = await api.post("/billing/request-renewal"); toast.success(r.data.message); await refreshMe(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const payOnline = async (lookupKey = "monthly_plan") => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Organization, currency & exchange rates" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6">
          <h3 className="font-display text-lg font-bold mb-4">Organization</h3>
          <form onSubmit={saveOrg} className="space-y-4">
            <div>
              <Label>Organization Name</Label>
              <Input className="rounded-none mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name-input" />
            </div>
            <div>
              <Label>Base Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="rounded-none mt-1" data-testid="base-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Reports & dashboard convert everything into this currency.</p>
            </div>
            <Button type="submit" disabled={saving} className="rounded-none" data-testid="save-settings-btn">{saving ? "Saving…" : "Save Changes"}</Button>
          </form>
        </div>

        <div className="border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Exchange Rates</h3>
            <span className="text-xs font-mono uppercase bg-secondary px-2 py-0.5">Base {fx.base}</span>
          </div>

          <Label>Rate source</Label>
          <Select value={fx.mode} onValueChange={setMode}>
            <SelectTrigger className="rounded-none mt-1 mb-4" data-testid="fx-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual (I set rates)</SelectItem>
              <SelectItem value="live">Live (auto-fetch)</SelectItem>
            </SelectContent>
          </Select>

          {fx.mode === "live" && (
            <Button type="button" variant="outline" className="rounded-none gap-2 mb-4" onClick={fetchLive} disabled={refreshing} data-testid="fx-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh live rates
            </Button>
          )}

          <p className="text-xs text-muted-foreground mb-3">Units of each currency per 1 {fx.base}. E.g. if 1 {fx.base} = 0.0012 USD, enter 0.0012.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {otherCurrencies.map((c) => (
              <div key={c.code} className="flex items-center gap-3">
                <span className="font-mono text-sm w-20">{c.symbol} {c.code}</span>
                <Input type="number" step="any" className="rounded-none flex-1" value={fx.rates?.[c.code] ?? ""} placeholder="—"
                  disabled={fx.mode === "live"} onChange={(e) => setRate(c.code, e.target.value)} data-testid={`fx-rate-${c.code}`} />
              </div>
            ))}
          </div>
          {fx.mode === "manual" && (
            <Button type="button" className="rounded-none mt-4" onClick={saveManualRates} data-testid="save-fx-btn">Save Rates</Button>
          )}
        </div>

        <div className="border border-border bg-card p-6" data-testid="logo-card">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><Upload className="w-4 h-4" /> Company Logo</h3>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 border border-border bg-secondary/40 flex items-center justify-center overflow-hidden">
              {user.organization?.logo_url
                ? <img src={user.organization.logo_url} alt="logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                : <span className="text-xs text-muted-foreground">No logo</span>}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              <Button type="button" variant="outline" className="rounded-none gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload logo"}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Shown in the sidebar & on invoice PDFs. PNG/JPG, under 3MB.</p>
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6" data-testid="billing-card">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Subscription</h3>
          <div className="space-y-1.5 font-mono text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="capitalize">{sub.plan || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="capitalize">{sub.is_expired ? "expired" : sub.status}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Renews / expires</span><span>{sub.expires_at ? sub.expires_at.slice(0, 10) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Days left</span><span>{sub.days_left ?? "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Payment mode</span><span className="capitalize">{sub.payment_mode}</span></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="rounded-none gap-2" onClick={requestRenewal} disabled={sub.renewal_requested} data-testid="request-renewal-btn"><Bell className="w-4 h-4" /> {sub.renewal_requested ? "Renewal requested" : "Request renewal"}</Button>
          </div>
          {sub.plan !== "demo" && (
            <div className="mt-5 border-t border-border pt-4">
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-3">Pay online & renew instantly</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button type="button" onClick={() => payOnline("monthly_plan")} data-testid="checkout-monthly-btn" className="border border-border p-3 text-left hover:border-primary transition-colors">
                  <div className="font-display font-bold">Monthly</div>
                  <div className="font-mono text-lg">$29<span className="text-xs text-muted-foreground">/mo</span></div>
                </button>
                <button type="button" onClick={() => payOnline("yearly_plan")} data-testid="checkout-yearly-btn" className="border border-primary p-3 text-left hover:bg-secondary transition-colors relative">
                  <div className="absolute top-1 right-1 text-[10px] font-mono bg-primary text-primary-foreground px-1.5">SAVE 17%</div>
                  <div className="font-display font-bold">Yearly</div>
                  <div className="font-mono text-lg">$290<span className="text-xs text-muted-foreground">/yr</span></div>
                </button>
                <button type="button" onClick={() => payOnline("oneoff_plan")} data-testid="checkout-oneoff-btn" className="border border-border p-3 text-left hover:border-primary transition-colors">
                  <div className="font-display font-bold">30-Day Access</div>
                  <div className="font-mono text-lg">$99<span className="text-xs text-muted-foreground">/once</span></div>
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Secured by Stripe · Test card 4242 4242 4242 4242</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
